import { useEffect } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiEndpoints } from 'src/constants/endpoints/apiEndpoints';

type ProjectIntakeInboxHubServer = {
  OnInboxChanged: () => void;
};

/**
 * ProjectIntakeInboxHub
 *
 * Listens for real-time updates to the Project Intake inbox and keeps
 * both the inbox list and all detail views in sync via React Query
 * invalidations.
 *
 * Initial behavior: any change event => invalidate inbox + details.
 * Future: add more RPC methods here when backend adds granular
 * events for assignments, updates, removals, etc.
 */
export const ProjectIntakeInboxHub = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.hubs.projectIntakeInbox)
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
      .build();

    const handleChange = async () => {
      await queryClient.invalidateQueries({
        queryKey: ['projectIntakeInbox'],
        type: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['projectIntakeDetail'],
        type: 'all'
      });
    };

    const rpc: ProjectIntakeInboxHubServer = {
      OnInboxChanged: handleChange
    };

    const procedures = Object.keys(rpc) as (keyof ProjectIntakeInboxHubServer)[];
    for (const procedure of procedures) {
      connection.on(procedure, rpc[procedure]);
    }

    connection.start().catch((e) => {
      Sentry.captureException(e);
    });

    connection.onreconnected(() => {
      void handleChange();
    });

    return () => {
      connection.stop().catch(() => {
        // Ignore stop errors on unmount
      });
    };
  }, [queryClient]);

  return null;
};
